import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

/**
 * Phase 84.22 · Sample Data System — client hook for demo status + mutations.
 *
 * Exposes:
 *   status  = { has_demo, has_real, demo_counts } | null while loading
 *   loading = true while any request is in flight
 *   refresh() — refetch the status
 *   seedDemo() — POST /api/onboarding/demo/seed; refreshes status on success
 *   clearDemo() — POST /api/onboarding/demo/clear; refreshes status on success
 *
 * All requests use the current session's JWT. On network or auth failures
 * the hook returns null status and logs a warning; the caller is expected
 * to degrade gracefully (banner won't show, import block won't fire).
 */
export function useDemoStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const authHeaders = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) throw new Error('no_session');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const refresh = useCallback(async () => {
    if (!backend) return null;
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const r = await fetch(`${backend}/api/onboarding/demo/status`, { headers });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const json = await r.json();
      setStatus(json);
      return json;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('demo status fetch failed', e);
      setError(String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [backend, authHeaders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const seedDemo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const r = await fetch(`${backend}/api/onboarding/demo/seed`, { method: 'POST', headers });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json?.detail?.message || `status ${r.status}`);
        return { ok: false, ...json };
      }
      await refresh();
      return { ok: true, ...json };
    } finally {
      setLoading(false);
    }
  }, [backend, authHeaders, refresh]);

  const clearDemo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const r = await fetch(`${backend}/api/onboarding/demo/clear`, { method: 'POST', headers });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(`status ${r.status}`);
        return { ok: false, ...json };
      }
      await refresh();
      return { ok: true, ...json };
    } finally {
      setLoading(false);
    }
  }, [backend, authHeaders, refresh]);

  return { status, loading, error, refresh, seedDemo, clearDemo };
}
